"use client";

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

export type AgentTestDraft = { id?: string; title: string; input: string; model_answer: string; rubric: string | null };

/** Remount with a fresh `key` each time it opens so the form starts from `test`. */
type AgentTestDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null to add a new test. */
  test: AgentTestDraft | null;
  description?: string;
  onSave: (test: AgentTestDraft) => Promise<boolean>;
};

const EMPTY = { title: "", input: "", model_answer: "", rubric: "" };

export function AgentTestDialog({ open, onOpenChange, test, onSave, description }: AgentTestDialogProps) {
  const [values, setValues] = useState(() =>
    test
      ? { title: test.title, input: test.input, model_answer: test.model_answer, rubric: test.rubric ?? "" }
      : EMPTY,
  );
  const [saving, setSaving] = useState(false);

  const canSave = useMemo(
    () => Boolean(values.title.trim() && values.input.trim() && values.model_answer.trim()) &&
      values.title.length <= 200 && values.input.length <= 20000 && values.model_answer.length <= 50000 && values.rubric.length <= 20000 && !saving,
    [saving, values],
  );

  const set = (key: keyof typeof EMPTY) => (event: { target: { value: string } }) =>
    setValues((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const saved = await onSave({ id: test?.id, ...values, rubric: values.rubric.trim() || null });
    setSaving(false);
    if (saved) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 p-0 sm:max-w-2xl">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle>{test?.id ? "Edit test" : "Add test"}</DialogTitle>
          <DialogDescription>
            {description ?? "The agent answers the question on its own, then the answer is scored against your model answer."}
          </DialogDescription>
        </DialogHeader>

        <form
          id="agent-test-form"
          className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground" htmlFor="agent-test-title">
              Title
            </label>
            <Input
              id="agent-test-title"
              maxLength={200}
              placeholder="e.g. Minimum pipe cover under a road"
              value={values.title}
              onChange={set("title")}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground" htmlFor="agent-test-input">
              Question
            </label>
            <Textarea
              id="agent-test-input"
              className="min-h-24"
              maxLength={20000}
              placeholder="What a user would ask the agent."
              value={values.input}
              onChange={set("input")}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground" htmlFor="agent-test-answer">
              Model answer
            </label>
            <Textarea
              id="agent-test-answer"
              className="min-h-32"
              maxLength={50000}
              placeholder="The answer you'd expect. Facts and conclusions matter; wording doesn't."
              value={values.model_answer}
              onChange={set("model_answer")}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground" htmlFor="agent-test-rubric">
              Rubric <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Textarea
              id="agent-test-rubric"
              className="min-h-20"
              maxLength={20000}
              placeholder="What must the answer include or avoid? e.g. Must cite the clause number. Must not recommend X."
              value={values.rubric}
              onChange={set("rubric")}
            />
          </div>
        </form>

        <DialogFooter className="border-t border-border px-6 py-4">
          <Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="agent-test-form" disabled={!canSave}>
            {saving ? <Spinner className="size-3.5" /> : null}
            {saving ? "Saving…" : test?.id ? "Save test" : "Add test"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
